/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WatchlistContextType } from '../types/watchlist';

const WATCHLIST_STORAGE_KEY = 'hot_watchlist_v1';

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export const useWatchlist = () => {
    const context = useContext(WatchlistContext);
    if (!context) {
        throw new Error('useWatchlist must be used within a WatchlistProvider');
    }
    return context;
};

export const WatchlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [watchlist, setWatchlist] = useState<string[]>([]);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(WATCHLIST_STORAGE_KEY);
            if (stored) {
                setWatchlist(JSON.parse(stored));
            }
        } catch (e) {
            console.error("Failed to load watchlist from storage", e);
        }
    }, []);

    const saveToStorage = (list: string[]) => {
        try {
            localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(list));
        } catch (e) {
            console.error("Failed to save watchlist to storage", e);
        }
    };

    const addToWatchlist = useCallback((mintAddress: string) => {
        setWatchlist(prev => {
            if (prev.includes(mintAddress)) return prev;
            const newList = [...prev, mintAddress];
            saveToStorage(newList);
            return newList;
        });
    }, []);

    const removeFromWatchlist = useCallback((mintAddress: string) => {
        setWatchlist(prev => {
            const newList = prev.filter(m => m !== mintAddress);
            saveToStorage(newList);
            return newList;
        });
    }, []);

    const isFavorited = useCallback((mintAddress: string) => {
        return watchlist.includes(mintAddress);
    }, [watchlist]);

    return (
        <WatchlistContext.Provider value={{ watchlist, addToWatchlist, removeFromWatchlist, isFavorited }}>
            {children}
        </WatchlistContext.Provider>
    );
};
